import { useEffect, useMemo, useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import {
	StyledArrow,
	StyledContainer,
	StyledDropdown,
	StyledDropdownItem,
	StyledDropdownList,
	StyledMultiInputWrapper,
	StyledSearchInput,
	StyledSearchWrapper,
} from './DonateItemDropdown.styled';
import { useDonateItemDropdown } from './useDonateItemDropdown';

interface DonateItemMultiSelectOption {
	value: string;
	label: string;
	category?: string;
}

interface DonateItemMultiSelectProps {
	options: DonateItemMultiSelectOption[];
	values: string[];
	onChange: (values: string[]) => void;
	placeholder?: string;
	filterPlaceholder?: string;
	singleSelectCategory?: string;
}

const DonateItemMultiSelect: React.FC<DonateItemMultiSelectProps> = ({
	options,
	values,
	onChange,
	placeholder = 'Выберите...',
	filterPlaceholder = 'Поиск...',
	singleSelectCategory,
}) => {
	const [debouncedFilter, setDebouncedFilter] = useState('');
	const {
		isOpen,
		filterText,
		filteredOptions: rawFilteredOptions,
		containerRef,
		inputRef,
		listRef,
		handleToggle,
		handleInputChange,
		handleInputKeyDown,
	} = useDonateItemDropdown(options, '', () => {});

	useEffect(() => {
		const timeoutId = setTimeout(() => setDebouncedFilter(filterText), 200);
		return () => clearTimeout(timeoutId);
	}, [filterText]);

	const filteredOptions = useMemo(() => {
		if (!debouncedFilter.trim()) return rawFilteredOptions;
		const lowerFilter = debouncedFilter.toLowerCase();
		return rawFilteredOptions.filter(
			(option) =>
				option.value.toLowerCase().includes(lowerFilter) ||
				option.label.toLowerCase().includes(lowerFilter),
		);
	}, [debouncedFilter, rawFilteredOptions]);

	const selectedLabels = useMemo(
		() =>
			values
				.map(
					(value) =>
						options.find((option) => option.value === value)?.label || value,
				)
				.join(', '),
		[options, values],
	);

	const selectOption = (option: DonateItemMultiSelectOption) => {
		playSound(Sound.LinkCursor);
		if (values.includes(option.value)) {
			onChange(values.filter((value) => value !== option.value));
		} else if (
			singleSelectCategory &&
			option.category === singleSelectCategory
		) {
			onChange([
				...values.filter(
					(value) =>
						options.find((otherOption) => otherOption.value === value)
							?.category !== singleSelectCategory,
				),
				option.value,
			]);
		} else {
			onChange([...values, option.value]);
		}
		setDebouncedFilter('');
	};

	return (
		<StyledContainer ref={containerRef}>
			<StyledMultiInputWrapper $focus={isOpen} onClick={handleToggle}>
				<Text
					style={{
						flex: 1,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{selectedLabels || placeholder}
				</Text>
				<StyledArrow $active={isOpen}>▼</StyledArrow>
			</StyledMultiInputWrapper>
			{isOpen && (
				<StyledDropdown>
					<StyledSearchWrapper>
						<StyledSearchInput
							ref={inputRef}
							value={filterText}
							onChange={handleInputChange}
							onKeyDown={handleInputKeyDown}
							placeholder={filterPlaceholder}
							autoFocus
						/>
					</StyledSearchWrapper>
					<StyledDropdownList ref={listRef}>
						{filteredOptions.length === 0 ? (
							<StyledDropdownItem disabled>
								<Text style={{ opacity: 0.5 }}>Ничего не найдено</Text>
							</StyledDropdownItem>
						) : (
							filteredOptions.map((option) => (
								<StyledDropdownItem
									key={option.value}
									$selected={values.includes(option.value)}
									onClick={(event) => {
										event.stopPropagation();
										selectOption(option);
									}}
									onMouseEnter={() => playSound(Sound.LinkCursor)}
								>
									<Text>{option.label}</Text>
									{option.value && (
										<Text style={{ opacity: 0.5, fontSize: 12, marginLeft: 8 }}>
											({option.value})
										</Text>
									)}
								</StyledDropdownItem>
							))
						)}
					</StyledDropdownList>
				</StyledDropdown>
			)}
		</StyledContainer>
	);
};

export { DonateItemMultiSelect };
